import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, History as IconeHistorico, Lock, UserPlus, Users } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { ehFutura } from "@/modules/escala/format";
import { CabecalhoEscala } from "@/modules/escala/components/CabecalhoEscala";
import { FaixaMinhaParticipacao } from "@/modules/escala/components/FaixaMinhaParticipacao";
import { FormularioEscala } from "@/modules/escala/components/FormularioEscala";
import { HistoricoEscala } from "@/modules/escala/components/HistoricoEscala";
import { ListaParticipantes } from "@/modules/escala/components/ListaParticipantes";
import { ModalAdicionarParticipante } from "@/modules/escala/components/ModalAdicionarParticipante";
import { ModalAlterarFuncao } from "@/modules/escala/components/ModalAlterarFuncao";
import { ResponderEscala } from "@/modules/escala/components/ResponderEscala";
import { ResumoRespostas } from "@/modules/escala/components/ResumoRespostas";
import {
  useEscalaDetalhe,
  usePodeGerirEscala,
  usePodeVerHistorico,
  useEscalaHistorico,
  useNomesPessoas,
  useEditarEscala,
  useCancelarEscala,
  useAdicionarParticipantes,
  useRemoverParticipante,
  useAlterarFuncao,
  useResponderEscala,
  useRegistrarRespostaAdministrativa,
} from "@/modules/escala/hooks/useEscalas";
import type { ParticipanteDetalhe } from "@/modules/escala/services/escalaService";

type Aba = "equipe" | "historico";
type RespostaEscolhida = "CONFIRMADA" | "RECUSADA";

export function DetalheEscalaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: detalhe, isLoading, isError } = useEscalaDetalhe(id);
  const { data: podeGerir = false } = usePodeGerirEscala(detalhe?.escala.ministerio_id);
  const { data: podeVerHistorico = false } = usePodeVerHistorico(id);
  const { data: historico, isLoading: carregandoHistorico } = useEscalaHistorico(id, podeVerHistorico);

  // Nomes do histórico: quem já está na equipe vem do próprio detalhe (não precisa consultar);
  // só o restante (ex.: pessoas já removidas, quem executou) é buscado em `pessoas`, que a RLS restringe.
  const nomesDaEquipe: Record<string, string> = {};
  for (const p of detalhe?.participantes ?? []) if (p.pessoaId) nomesDaEquipe[p.pessoaId] = p.nome;
  const idsDoHistorico = (historico ?? [])
    .flatMap((e) => [e.pessoa_id, e.executado_por_pessoa_id])
    .filter((pessoaId): pessoaId is string => !!pessoaId && !nomesDaEquipe[pessoaId]);
  const { data: nomesBuscados, isLoading: carregandoNomes } = useNomesPessoas(idsDoHistorico);

  const editar = useEditarEscala();
  const cancelar = useCancelarEscala();
  const adicionar = useAdicionarParticipantes();
  const remover = useRemoverParticipante();
  const alterarFuncao = useAlterarFuncao();
  const responder = useResponderEscala();
  const respostaAdministrativa = useRegistrarRespostaAdministrativa();

  const [aba, setAba] = useState<Aba>("equipe");
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [erroEditar, setErroEditar] = useState<string | null>(null);
  const [modalAdicionarAberto, setModalAdicionarAberto] = useState(false);
  const [erroAdicionar, setErroAdicionar] = useState<string | null>(null);
  const [paraCancelar, setParaCancelar] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [erroCancelar, setErroCancelar] = useState<string | null>(null);
  const [paraRemover, setParaRemover] = useState<ParticipanteDetalhe | null>(null);
  const [erroRemover, setErroRemover] = useState<string | null>(null);
  const [paraAlterarFuncao, setParaAlterarFuncao] = useState<ParticipanteDetalhe | null>(null);
  const [erroAlterarFuncao, setErroAlterarFuncao] = useState<string | null>(null);
  const [respondendoModo, setRespondendoModo] = useState<"propria" | "administrativa" | null>(null);
  const [participanteRespondendo, setParticipanteRespondendo] = useState<ParticipanteDetalhe | null>(null);
  const [resultadoResposta, setResultadoResposta] = useState<RespostaEscolhida | null>(null);
  const [erroResponder, setErroResponder] = useState<string | null>(null);

  if (isLoading) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <p className="text-center text-sm text-muted">Carregando escala...</p>
      </main>
    );
  }

  // "Não encontrado" e "sem permissão" tratados de forma IDÊNTICA (MOV-027/033) — nunca vazar qual dos dois é o caso real.
  if (isError || !detalhe) {
    return (
      <main className="mx-auto flex w-full max-w-3xl flex-col items-center gap-3 px-4 py-8 text-center sm:px-6 sm:py-10">
        <p className="text-sm text-muted">Esta escala não foi encontrada.</p>
        <button
          type="button"
          onClick={() => navigate("/escalas")}
          className="flex items-center gap-1.5 text-sm font-semibold text-primary"
        >
          <ArrowLeft size={16} /> Voltar para Escala
        </button>
      </main>
    );
  }

  const { escala, participantes } = detalhe;
  const ativa = escala.situacao === "ativa";
  const futura = ativa && ehFutura(escala.data_hora);
  const gerirAtiva = podeGerir && ativa;
  // `ehPropria` vem do banco (γ — ESC-DEC-006): `pessoaId` dos colegas chega nulo para o membro comum.
  const minhaParticipacao = participantes.find((p) => p.ehPropria) ?? null;
  // Há colega com status mascarado pelo banco: avisa por que não aparece (só a liderança vê).
  const algumStatusOculto = participantes.some((p) => !p.ehPropria && !p.respostaVisivel);
  const nomes = { ...nomesBuscados, ...nomesDaEquipe };

  async function aoSalvarEdicao(dados: { nome: string; descricao: string | null; dataHoraISO: string }) {
    if (!id) return;
    setErroEditar(null);
    try {
      await editar.mutateAsync({
        id,
        dados: { versao: escala.versao, nome: dados.nome, descricao: dados.descricao, dataHora: dados.dataHoraISO },
      });
      setModalEditarAberto(false);
    } catch (e) {
      setErroEditar(e instanceof Error ? e.message : "Não foi possível editar a escala.");
    }
  }

  async function confirmarCancelamento() {
    if (!id) return;
    setErroCancelar(null);
    try {
      await cancelar.mutateAsync({ id, versao: escala.versao, motivo: motivoCancelamento.trim() || null });
      setParaCancelar(false);
      setMotivoCancelamento("");
    } catch (e) {
      setErroCancelar(e instanceof Error ? e.message : "Não foi possível cancelar a escala.");
    }
  }

  async function aoConfirmarAdicionar(itens: { pessoaId: string; funcaoId: string }[]) {
    if (!id) return;
    setErroAdicionar(null);
    try {
      await adicionar.mutateAsync({ escalaId: id, itens });
      setModalAdicionarAberto(false);
    } catch (e) {
      setErroAdicionar(e instanceof Error ? e.message : "Não foi possível adicionar os participantes.");
    }
  }

  async function confirmarRemocao() {
    if (!id || !paraRemover) return;
    setErroRemover(null);
    try {
      await remover.mutateAsync({ escalaId: id, participacaoId: paraRemover.id });
      setParaRemover(null);
    } catch (e) {
      setErroRemover(e instanceof Error ? e.message : "Não foi possível remover o participante.");
    }
  }

  async function confirmarAlteracaoFuncao(novaFuncaoId: string) {
    if (!id || !paraAlterarFuncao) return;
    setErroAlterarFuncao(null);
    try {
      await alterarFuncao.mutateAsync({ escalaId: id, participacaoId: paraAlterarFuncao.id, novaFuncaoId });
      setParaAlterarFuncao(null);
    } catch (e) {
      setErroAlterarFuncao(e instanceof Error ? e.message : "Não foi possível alterar a função.");
    }
  }

  function abrirResponder(modo: "propria" | "administrativa", participante: ParticipanteDetalhe | null) {
    setRespondendoModo(modo);
    setParticipanteRespondendo(participante);
    setResultadoResposta(null);
    setErroResponder(null);
  }

  function fecharResponder() {
    setRespondendoModo(null);
    setParticipanteRespondendo(null);
    setResultadoResposta(null);
    setErroResponder(null);
  }

  async function aoResponder(resposta: RespostaEscolhida, justificativa: string | null) {
    if (!id || !participanteRespondendo) return;
    setErroResponder(null);
    try {
      if (respondendoModo === "propria") {
        await responder.mutateAsync({ escalaId: id, participacaoId: participanteRespondendo.id, resposta, justificativa });
      } else {
        await respostaAdministrativa.mutateAsync({
          escalaId: id,
          participacaoId: participanteRespondendo.id,
          resposta,
          justificativa,
        });
      }
      // Mantém o modal aberto mostrando o resultado; quem fecha é o "Concluir".
      setResultadoResposta(resposta);
    } catch (e) {
      setErroResponder(e instanceof Error ? e.message : "Não foi possível registrar a resposta.");
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8 sm:px-6 sm:py-10">
      <button
        type="button"
        onClick={() => navigate("/escalas")}
        className="flex min-h-11 w-fit items-center gap-1.5 text-sm font-semibold text-muted hover:text-text"
      >
        <ArrowLeft size={16} aria-hidden /> Escala
      </button>

      <CabecalhoEscala
        escala={escala}
        onEditar={
          gerirAtiva
            ? () => {
                setErroEditar(null);
                setModalEditarAberto(true);
              }
            : undefined
        }
        onCancelar={
          gerirAtiva
            ? () => {
                setErroCancelar(null);
                setParaCancelar(true);
              }
            : undefined
        }
      />

      {minhaParticipacao && ativa && (
        <FaixaMinhaParticipacao
          participante={minhaParticipacao}
          podeResponder={futura}
          onResponder={() => abrirResponder("propria", minhaParticipacao)}
        />
      )}

      <div role="tablist" aria-label="Seções da escala" className="flex gap-1 border-b border-border">
        <button
          type="button"
          role="tab"
          id="aba-equipe"
          aria-selected={aba === "equipe"}
          aria-controls="painel-equipe"
          onClick={() => setAba("equipe")}
          className={`-mb-px inline-flex min-h-11 items-center gap-1.5 border-b-2 px-3.5 text-sm font-semibold transition-colors ${
            aba === "equipe" ? "border-primary text-text" : "border-transparent text-muted hover:text-text"
          }`}
        >
          <Users size={16} aria-hidden className={aba === "equipe" ? "text-primary" : ""} />
          Equipe
          <span className="rounded-full bg-surface-2 px-1.5 text-xs font-bold text-muted">{participantes.length}</span>
        </button>
        {podeVerHistorico && (
          <button
            type="button"
            role="tab"
            id="aba-historico"
            aria-selected={aba === "historico"}
            aria-controls="painel-historico"
            onClick={() => setAba("historico")}
            className={`-mb-px inline-flex min-h-11 items-center gap-1.5 border-b-2 px-3.5 text-sm font-semibold transition-colors ${
              aba === "historico" ? "border-primary text-text" : "border-transparent text-muted hover:text-text"
            }`}
          >
            <IconeHistorico size={16} aria-hidden className={aba === "historico" ? "text-primary" : ""} />
            Histórico
          </button>
        )}
      </div>

      {aba === "equipe" && (
        <div id="painel-equipe" role="tabpanel" aria-labelledby="aba-equipe" className="flex flex-col gap-3">
          {algumStatusOculto && (
            <p className="flex items-start gap-2.5 rounded-xl bg-info/10 px-3.5 py-3 text-[13.5px] leading-snug text-text">
              <Lock size={18} aria-hidden className="mt-px shrink-0 text-info" />
              <span>
                Você vê quem está na equipe e a função de cada um. A resposta dos colegas é visível só para a
                liderança.
              </span>
            </p>
          )}

          <ResumoRespostas participantes={participantes} />

          {gerirAtiva && (
            <button
              type="button"
              onClick={() => {
                setErroAdicionar(null);
                setModalAdicionarAberto(true);
              }}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover"
            >
              <UserPlus size={18} aria-hidden /> Escalar participantes
            </button>
          )}

          <ListaParticipantes
            participantes={participantes}
            onAlterarFuncao={
              gerirAtiva
                ? (p) => {
                    setErroAlterarFuncao(null);
                    setParaAlterarFuncao(p);
                  }
                : undefined
            }
            onRemover={gerirAtiva ? (p) => setParaRemover(p) : undefined}
            // O banco só aceita registrar resposta enquanto a escala está ativa e ainda por acontecer.
            onRegistrarResposta={podeGerir && futura ? (p) => abrirResponder("administrativa", p) : undefined}
          />
        </div>
      )}

      {aba === "historico" && podeVerHistorico && (
        <div id="painel-historico" role="tabpanel" aria-labelledby="aba-historico">
          {carregandoHistorico || carregandoNomes ? (
            <p className="py-6 text-center text-sm text-muted">Carregando histórico...</p>
          ) : (
            <HistoricoEscala
              eventos={historico ?? []}
              nomes={nomes}
              motivoCancelamento={escala.motivo_cancelamento}
            />
          )}
        </div>
      )}

      <Modal titulo="Editar escala" aberto={modalEditarAberto} onClose={() => setModalEditarAberto(false)} carregando={editar.isPending}>
        <FormularioEscala
          escalaEmEdicao={escala}
          carregando={editar.isPending}
          erro={erroEditar}
          onSalvar={aoSalvarEdicao}
          onCancelar={() => setModalEditarAberto(false)}
        />
      </Modal>

      <ModalConfirmacao
        aberto={paraCancelar}
        titulo="Cancelar escala"
        mensagem={`Tem certeza que deseja cancelar "${escala.nome}"? Os participantes ativos serão notificados. Esta ação não pode ser desfeita.`}
        textoBotaoConfirmar="Cancelar escala"
        carregando={cancelar.isPending}
        erro={erroCancelar}
        onConfirmar={confirmarCancelamento}
        onCancelar={() => {
          setParaCancelar(false);
          setMotivoCancelamento("");
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label htmlFor="motivo-cancelamento" className="text-sm font-medium tracking-wide text-text">
            Motivo <span className="font-normal text-muted">(opcional)</span>
          </label>
          <textarea
            id="motivo-cancelamento"
            value={motivoCancelamento}
            onChange={(e) => setMotivoCancelamento(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </ModalConfirmacao>

      <ModalConfirmacao
        aberto={!!paraRemover}
        titulo="Remover participante"
        mensagem={`Remover ${paraRemover?.nome ?? ""} desta escala? A pessoa será notificada.`}
        textoBotaoConfirmar="Remover"
        carregando={remover.isPending}
        erro={erroRemover}
        onConfirmar={confirmarRemocao}
        onCancelar={() => setParaRemover(null)}
      />

      {/* Monta só com participante escolhido: fechado, não consulta as funções do ministério. */}
      {paraAlterarFuncao && (
        <ModalAlterarFuncao
          participante={paraAlterarFuncao}
          ministerioId={escala.ministerio_id}
          carregando={alterarFuncao.isPending}
          erro={erroAlterarFuncao}
          onConfirmar={confirmarAlteracaoFuncao}
          onCancelar={() => setParaAlterarFuncao(null)}
        />
      )}

      {/* Monta o modal só aberto e só para quem pode gerir: fechado, ele não consulta candidatos (B3, 02/10/2026). */}
      {id && podeGerir && modalAdicionarAberto && (
        <ModalAdicionarParticipante
          aberto={modalAdicionarAberto}
          escalaId={id}
          ministerioId={escala.ministerio_id}
          carregando={adicionar.isPending}
          erro={erroAdicionar}
          onConfirmar={aoConfirmarAdicionar}
          onCancelar={() => setModalAdicionarAberto(false)}
        />
      )}

      <ResponderEscala
        aberto={!!respondendoModo}
        modo={respondendoModo ?? "propria"}
        nomeParticipante={respondendoModo === "administrativa" ? (participanteRespondendo?.nome ?? undefined) : undefined}
        funcaoParticipante={participanteRespondendo?.funcaoNome}
        escalaNome={escala.nome}
        escalaDataHora={escala.data_hora}
        ministerioNome={escala.ministerioNome}
        carregando={responder.isPending || respostaAdministrativa.isPending}
        erro={erroResponder}
        resultado={resultadoResposta}
        onResponder={aoResponder}
        onFechar={fecharResponder}
      />
    </main>
  );
}
